import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import DeleteUserModal from "./modals/DeleteUserModal";
import AddUserModal from "./modals/AddUserModal";
import { fetchUsers, addUser, updateUser, deleteUser } from "../services/userManagement";

function StatusBadge({ status }) {
  const isActive = status === "Active";
  return (
    <span
      className={`inline-block align-middle h-[26px] leading-[28px] px-[10px] rounded-full text-xs select-none whitespace-nowrap ${isActive ? "bg-[#edf6ee] text-[#158723]" : "bg-[#feefef] text-[#E83434]"
        }`}
    >
      {status}
    </span>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [addModal, setAddModal] = useState({ isOpen: false, editUser: null });
  const [searchQuery, setSearchQuery] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersArray = await fetchUsers({
        skip: (page - 1) * 10,
        limit: 10,
        search: searchQuery,
      });

      console.log("usersArray:", usersArray);

      setUsers(Array.isArray(usersArray) ? usersArray : []);
      setTotalPages(Math.ceil(usersArray.length / 10) || 1);
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  // Load users whenever page or search changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleDeleteConfirm = async () => {
    try {
      if (deleteModal.user) {
        await deleteUser(deleteModal.user.email); // assuming API uses email or id
        await loadUsers();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleteModal({ isOpen: false, user: null });
    }
  };

  const handleSaveUser = async (formData) => {
    try {
      if (addModal.editUser) {
        await updateUser({ ...formData, user_id: addModal.editUser.email });
      } else {
        await addUser(formData);
      }
      await loadUsers();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setAddModal({ isOpen: false, editUser: null });
    }
  };

  if (loading) return <div className="p-4 text-center">Loading users...</div>;

  return (
    <>
      {/* Search + Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
        <h2 className="text-lg font-semibold select-none m-0">User Management</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* TODO */}
          {/* <div className="relative flex-1 sm:flex-initial">
            <input
              type="search"
              placeholder="Search by Email"
              value={searchQuery}
              onChange={(e) => {
                setPage(1); // reset page on new search
                setSearchQuery(e.target.value);
              }}
              className="w-full h-[40px] sm:w-[350px] border border-gray-300 rounded-md pl-10 pr-4 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              aria-label="Search"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div> */}
          <button
            onClick={() => setAddModal({ isOpen: true, editUser: null })}
            className="bg-teal-600 hover:bg-teal-700 h-[40px] text-white font-[500] px-5 py-0 rounded-md transition-colors whitespace-nowrap"
          >
            Add User
          </button>
        </div>
      </div>

      {/* User Table */}
      <section>
        <div className="overflow-x-auto rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F4F4F4] text-gray-600 font-semibold">
              <tr>
                <th className="px-4 py-3 text-left">No.</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr key={user.email}>
                    <td className="px-4 py-3">{index + 1 + (page - 1) * 10}</td>
                    <td className="px-4 py-3">{user.username}</td>
                    <td className="px-4 py-3">{user.email}</td>
                    <td className="px-4 py-3">{user.role}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.verified ? "Active" : "Inactive"} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button onClick={() => setDeleteModal({ isOpen: true, user })}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pagination */}
      <nav className="mt-4 flex justify-center items-center gap-2 text-sm select-none">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-teal-100"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => setPage(i + 1)}
            className={`w-8 h-8 rounded border border-gray-300 transition-colors ${page === i + 1 ? "bg-teal-700 text-white" : "hover:bg-teal-100"
              }`}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
          className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-teal-100"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </nav>

      {/* Modals */}
      <DeleteUserModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={handleDeleteConfirm}
        userName={deleteModal.user?.username}
      />
      <AddUserModal
        isOpen={addModal.isOpen}
        onClose={() => setAddModal({ isOpen: false, editUser: null })}
        onSave={handleSaveUser}
        editUser={addModal.editUser}
      />
    </>
  );
}
